import { CallType, callTypeFromCode } from './CallType'
import { Coordinate, coordinateIsInvalid } from './Coordinate'
import { DeterminantCodeCategory, determinantCodeCategoryFromCode } from './DeterminantCodeCategory'
import {
  arrayFromJSONValue,
  booleanFromJSONValue,
  coordinateFromJSONValues,
  dateOptionalFromJSONValue,
  intFromJSONValue,
  intOptionalFromJSONValue,
  stringFromJSONValue
} from './parser'
import { UnitModel } from './UnitModel'
import { IncidentNarrativeItemModel } from './IncidentNarrativeItemModel'
import { prettyDate } from './PrettyDate'
import { UnitStatus } from './UnitStatus'
import { IncidentPostModel } from './IncidentPostModel'

export class IncidentModel {
  json: string
  recordID: string
  isProData: boolean
  incidentNumber: string
  callType: CallType
  determinantCodeCategory?: DeterminantCodeCategory
  alarmLevel: number
  agencyID: string
  callReceivedDateTime?: Date
  closedDateTime?: Date
  fullDisplayAddress: string
  commonPlaceName: string
  units: UnitModel[]
  addressTruncated: boolean
  isShareable: boolean
  locationCoordinate: Coordinate
  agencyIncidentCallTypeDescription: string
  agencyIncidentCallType: string
  jurisdiction: string
  agencyDeterminantCode: string
  tacticalChannel: string
  publicLocation: boolean
  priority: string
  commandName: string
  firstDueStation: string
  locationComment: string
  disptchDateTime?: Date
  enrouteDateTime?: Date
  onSceneDateTime?: Date
  workingFireDateTime?: Date
  underControlDateTime?: Date
  crossStreet1: string
  crossStreet2: string
  archived: boolean
  narrative: IncidentNarrativeItemModel[]
  posts: IncidentPostModel[]
  locationHistory: IncidentModel[]
  suppressInApp: boolean
  submissionDelay?: number
  dispatchSubmissionDelay?: number

  public constructor(
    json: string,
    recordID: string,
    isProData: boolean,
    incidentNumber: string,
    callType: CallType,
    determinantCodeCategory: DeterminantCodeCategory | undefined,
    alarmLevel: number,
    agencyID: string,
    callReceivedDateTime: Date | undefined,
    closedDateTime: Date | undefined,
    fullDisplayAddress: string,
    commonPlaceName: string,
    units: UnitModel[],
    addressTruncated: boolean,
    isShareable: boolean,
    locationCoordinate: Coordinate,
    agencyIncidentCallTypeDescription: string,
    agencyIncidentCallType: string,
    jurisdiction: string,
    agencyDeterminantCode: string,
    tacticalChannel: string,
    publicLocation: boolean,
    priority: string,
    commandName: string,
    firstDueStation: string,
    locationComment: string,
    disptchDateTime: Date | undefined,
    enrouteDateTime: Date | undefined,
    onSceneDateTime: Date | undefined,
    workingFireDateTime: Date | undefined,
    underControlDateTime: Date | undefined,
    crossStreet1: string,
    crossStreet2: string,
    archived: boolean,
    narrative: IncidentNarrativeItemModel[],
    posts: IncidentPostModel[],
    locationHistory: IncidentModel[],
    suppressInApp: boolean,
    submissionDelay?: number,
    dispatchSubmissionDelay?: number
  ) {
    this.json = json
    this.recordID = recordID
    this.isProData = isProData
    this.incidentNumber = incidentNumber
    this.callType = callType
    this.determinantCodeCategory = determinantCodeCategory
    this.alarmLevel = alarmLevel //TODO
    this.agencyID = agencyID
    this.callReceivedDateTime = callReceivedDateTime
    this.closedDateTime = closedDateTime
    this.fullDisplayAddress = fullDisplayAddress
    this.commonPlaceName = commonPlaceName
    this.units = units
    this.addressTruncated = addressTruncated
    this.isShareable = isShareable
    this.locationCoordinate = locationCoordinate
    this.agencyIncidentCallTypeDescription = agencyIncidentCallTypeDescription
    this.agencyIncidentCallType = agencyIncidentCallType
    this.jurisdiction = jurisdiction
    this.agencyDeterminantCode = agencyDeterminantCode
    this.tacticalChannel = tacticalChannel
    this.publicLocation = publicLocation
    this.priority = priority
    this.commandName = commandName
    this.firstDueStation = firstDueStation
    this.locationComment = locationComment
    this.disptchDateTime = disptchDateTime
    this.enrouteDateTime = enrouteDateTime
    this.onSceneDateTime = onSceneDateTime
    this.workingFireDateTime = workingFireDateTime
    this.underControlDateTime = underControlDateTime
    this.crossStreet1 = crossStreet1
    this.crossStreet2 = crossStreet2
    this.archived = archived
    this.narrative = narrative
    this.posts = posts
    this.locationHistory = locationHistory
    this.suppressInApp = suppressInApp
    this.submissionDelay = submissionDelay
    this.dispatchSubmissionDelay = dispatchSubmissionDelay

    if (closedDateTime) {
      this.units.forEach(unit => (unit.overrideStatusToCleared = true))
    }
  }

  public static modelFromJSON(json: any): IncidentModel {
    return new IncidentModel(
      JSON.stringify(json, null, 2),
      stringFromJSONValue(json['ID']),
      booleanFromJSONValue(json['IsProData']),
      stringFromJSONValue(json['IncidentNumber']),
      callTypeFromCode(stringFromJSONValue(json['PulsePointIncidentCallType'])),
      determinantCodeCategoryFromCode(stringFromJSONValue(json['PulsePointDeterminantCodeCategory'])),
      intFromJSONValue(json['AlarmLevel']),
      stringFromJSONValue(json['AgencyID']),
      dateOptionalFromJSONValue(json['CallReceivedDateTime']),
      dateOptionalFromJSONValue(json['ClosedDateTime']),
      stringFromJSONValue(json['FullDisplayAddress']),
      stringFromJSONValue(json['CommonPlaceName']),
      arrayFromJSONValue(json['Unit']).map(value => UnitModel.modelFromJSON(value)),
      booleanFromJSONValue(json['AddressTruncated']),
      booleanFromJSONValue(json['IsShareable']),
      coordinateFromJSONValues(json['Latitude'], json['Longitude']),
      stringFromJSONValue(json['AgencyIncidentCallTypeDescription']),
      stringFromJSONValue(json['AgencyIncidentCallType']),
      stringFromJSONValue(json['Jurisdiction']),
      stringFromJSONValue(json['AgencyDeterminantCode']),
      stringFromJSONValue(json['TacticalChannel']),
      booleanFromJSONValue(json['PublicLocation']),
      stringFromJSONValue(json['Priority']),
      stringFromJSONValue(json['CommandName']),
      stringFromJSONValue(json['FirstDueStation']),
      stringFromJSONValue(json['LocationComment']),
      dateOptionalFromJSONValue(json['DispatchDateTime']),
      dateOptionalFromJSONValue(json['EnrouteDateTime']),
      dateOptionalFromJSONValue(json['OnSceneDateTime']),
      dateOptionalFromJSONValue(json['WorkingFireDateTime']),
      dateOptionalFromJSONValue(json['UnderControlDateTime']),
      stringFromJSONValue(json['CrossStreet1']),
      stringFromJSONValue(json['CrossStreet2']),
      booleanFromJSONValue(json['Archived']),
      arrayFromJSONValue(json['Narrative'])
        .map(value => IncidentNarrativeItemModel.modelFromJSON(value))
        .sort((a, b) => b.dateTime.getTime() - a.dateTime.getTime()),
      arrayFromJSONValue(json['Posts']).map(value => IncidentPostModel.modelFromJSON(value)),
      arrayFromJSONValue(json['LocationHistory']).map(value => IncidentModel.modelFromJSON(value)),
      booleanFromJSONValue(json['SuppressInApp']),
      intOptionalFromJSONValue(json['SubmissionDelay']),
      intOptionalFromJSONValue(json['DispatchSubmissionDelay'])
    )
  }

  public listIconURL() {
    return `/images/respond_icons/${this.callType.id.toLowerCase()}_list.png`
  }

  public mapPinIconURL() {
    if (this.closedDateTime !== undefined) {
      return `/images/respond_icons/${this.callType.id.toLowerCase()}_map_recent.png`
    } else {
      return `/images/respond_icons/${this.callType.id.toLowerCase()}_map_active.png`
    }
  }

  public mapPinIconSize() {
    if (this.closedDateTime !== undefined) {
      return 48
    } else {
      return 72
    }
  }

  public isMappable() {
    return coordinateIsInvalid(this.locationCoordinate)
  }

  public callTypeDescription() {
    return this.determinantCodeCategory?.description ?? this.callType.description
  }

  public alarmLevelDescription() {
    return this.alarmLevel >= 2 && this.alarmLevel <= 9 && this.callType.category === 'Fire'
      ? `${this.alarmLevel} Alarm`
      : null
  }

  public incidentPostsDescription() {
    if (this.posts.length <= 0) {
      return null
    } else if (this.posts.length === 1) {
      return '1 Post'
    } else {
      return `${this.posts.length} Posts`
    }
  }

  public formattedCallRecivedDateTime(timeZone: string | undefined, includeDate: boolean) {
    return prettyDate(this.callReceivedDateTime, timeZone, timeZone !== undefined, includeDate)
  }

  public formattedAddress() {
    const crossStreets = [this.crossStreet1, this.crossStreet2].filter(value => value !== '').join(', ')
    if (crossStreets === '') {
      return this.fullDisplayAddress
    } else {
      return [this.fullDisplayAddress, `X: ${crossStreets}`].join('\n')
    }
  }

  public formattedTacticalChannel() {
    if (this.tacticalChannel === '') {
      return ''
    }
    return `Channel: ${this.tacticalChannel}`
  }

  public formattedPriority() {
    if (this.priority === '') {
      return ''
    }
    return `Priority: ${this.priority}`
  }

  public formattedUnits(category: UnitStatus) {
    let unitsWithoutTransportLocation = this.units
      .filter(unit => unit.statusCategory() === category && unit.resolvedTransportLocation() === '')
      .map(unit => unit.formattedUnitDescription())
    let unitsWithTransportLocation = this.units
      .filter(unit => unit.statusCategory() === category && unit.resolvedTransportLocation() !== '')
      .map(unit => unit.formattedUnitDescription())
    let firstLine = unitsWithoutTransportLocation.join(', ')
    let lines = firstLine === '' ? unitsWithTransportLocation : [firstLine].concat(unitsWithTransportLocation)
    return lines.join('\n')
  }

  public formattedElapsedTime(shortNames: boolean) {
    const singularMinuteLabel = shortNames ? 'm' : ' minute'
    const pluralMinuteLabel = shortNames ? 'm' : ' minutes'
    const singularHourLabel = shortNames ? 'h' : ' hour'
    const pluralHourLabel = shortNames ? 'h' : ' hours'

    const callTime = this.callReceivedDateTime?.getTime()
    const closedTime = this.closedDateTime?.getTime()
    if (callTime !== undefined && closedTime !== undefined) {
      const ms = Math.abs(closedTime - callTime)

      const hourDiff = Math.floor(ms / (60 * 60 * 1000))
      const minuteDiff = Math.floor(ms / (60 * 1000)) - hourDiff * 60
      const minutes = minuteDiff === 1 ? `1${singularMinuteLabel}` : `${minuteDiff}${pluralMinuteLabel}`
      const hours = hourDiff === 1 ? `1${singularHourLabel}` : `${hourDiff}${pluralHourLabel}`
      const negativeSign = callTime > closedTime ? '-' : ''
      if (hourDiff > 0 && minuteDiff > 0) {
        return `${negativeSign}${hours} ${minutes}`
      } else if (hourDiff > 0) {
        return `${negativeSign}${hours}`
      } else {
        return `${negativeSign}${minutes}`
      }
    }
    return ''
  }

  public matchesOnLowerCaseSearchTerms(searchTerms: string[]) {
    if (searchTerms.length === 0) {
      return true
    }

    const addressTerms = this.fullDisplayAddress.toLowerCase().split(/[^A-Za-z0-9]/)
    const unitIDs = this.units.map(unit => unit.id.toLowerCase())
    return (
      searchTerms.every(
        term =>
          unitIDs.some(unitID => unitID.startsWith(term)) ||
          addressTerms.some(addressTerm => addressTerm.startsWith(term))
      ) ||
      (searchTerms.length === 1 && this.incidentNumber !== '' && searchTerms[0] === this.incidentNumber.toLowerCase())
    )
  }
}
